<script lang="ts" setup>
/**
 * 单行文本自适应缩放:内容超出可用宽度时,对文本整体 transform: scale 等比缩小,
 * 保证完整展示且不折行;内容或容器尺寸变化时自动重算。
 * 缩放只影响视觉大小,占位高度保持原字号行高不变,不引起周边布局跳动。
 */
import { onBeforeUnmount, onMounted, ref } from 'vue'

const containerRef = ref<HTMLElement>()
const contentRef = ref<HTMLElement>()
const scale = ref(1)

let observer: ResizeObserver | null = null

function updateScale() {
  const available = containerRef.value?.clientWidth ?? 0
  // 内层为 nowrap 的 inline-block,scrollWidth 即文本自然宽度(不受缩放影响)
  const natural = contentRef.value?.scrollWidth ?? 0
  scale.value = natural > available && natural > 0 ? available / natural : 1
}

onMounted(() => {
  updateScale()
  // transform 不改变布局尺寸,缩放不会反向触发 observer,无循环风险
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(updateScale)
    observer.observe(containerRef.value!)
    observer.observe(contentRef.value!)
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<template>
  <div ref="containerRef" class="basic-fit-text">
    <span
      ref="contentRef"
      class="basic-fit-text__content"
      :style="scale < 1 ? { transform: `scale(${scale})` } : undefined"
    >
      <slot />
    </span>
  </div>
</template>

<style scoped>
.basic-fit-text {
  /* 允许作为 flex 子项被压缩,压缩后的容器宽与内容自然宽之比即缩放比例 */
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.basic-fit-text__content {
  display: inline-block;
  white-space: nowrap;
  transform-origin: left center;
}
</style>
